/* eslint-disable no-useless-escape */
/* eslint-disable no-param-reassign */
/* eslint-disable jsx-a11y/label-has-for */
/* eslint-disable import/extensions */
/* eslint-disable import/no-unresolved */
import * as React from "react";
import ScaleLoader from "react-spinners/ScaleLoader";
import { datadogRum } from "@datadog/browser-rum";

import * as API from "../../../../api/manageCatalog";
import AutocompleteInputField from "../../../../components/reusable/autocompleteInputField";
import Checkbox from "../../../../components/reusable/checkbox";
import CheckboxGroupInputField from "../../../../components/reusable/checkboxGroupInputField";
import DeleteConfirmModal from "./deleteConfirmModal";
import Denominations from "./denominations";
import Description from "./description";
import FileUploadField from "../../../../components/reusable/fileUploadField";
import RadioGroupInputField from "../../../../components/reusable/radioGroupInputField";
import SelectInputField from "../../../../components/reusable/selectInputField";
import TextInputField from "../../../../components/reusable/textInputField";
import ToastMessage from "../../../../components/reusable/toastMessage";
import Warnings from "./warnings";
import {
  Category,
  Country,
  Currency,
  CatalogItem,
  CatalogItemDetailsPropTypes,
  ToastMessageOptionTypes,
  RedemptionMethod,
} from "../lib/types";
import {
  StyledAlertTextBlock,
  StyledBackLink,
  StyledButton100,
  StyledButtonContainerHalf,
  StyledButtonDeleteItem,
  StyledCheckboxWrapper,
  StyledContainer,
  StyledErrorMessage,
  StyledForm,
  StyledFormField,
  StyledHelperTextWithMargin,
  StyledInstruction,
  StyledLabel,
  StyledPageHeader,
  StyledPageLoaderContainer,
  StyledParagraphNoMargin,
  StyledRequired,
} from "../lib/styles";
import {
  countriesRadioOptions,
  defaultCatalogItemDetails,
  defaultMessageOptions,
  distributionMethods,
  externalIdentifierProviders,
  legacyIdProviders,
  providers,
  redemptionLocationMethods,
  redemptionMethodProviders,
  twitterHandleDistributionMethods,
  websiteDistributionMethods,
  websiteRedemptionMethods,
} from "../lib/constants";
import {
  claimUrlIsRequired,
  getErrorMessage,
  getFormattedData,
  getRequiredFields,
  hasValue,
} from "../lib/helpers";
import { theme } from "../../../theme/theme";

const CatalogItemDetails = ({
  id,
}: CatalogItemDetailsPropTypes): React.ReactElement => {
  const [errorMessage, setErrorMessage] = React.useState<string>("");
  const [loading, setLoading] = React.useState<boolean>(true);
  const [modal, setModal] = React.useState<string>("");
  const [categories, setCategories] = React.useState<Category[]>([]);
  const [countries, setCountries] = React.useState<Country[]>([]);
  const [currencies, setCurrencies] = React.useState<Currency[]>([]);
  const [redemptionMethods, setRedemptionMethods] = React.useState<
    RedemptionMethod[]
  >([]);
  const [itemDetails, setItemDetails] = React.useState<CatalogItem>(
    // Note: using a deep copy so the constant is not modified
    JSON.parse(JSON.stringify(defaultCatalogItemDetails))
  );

  const [
    toastMessageOptions,
    setToastMessageOptions,
  ] = React.useState<ToastMessageOptionTypes>(defaultMessageOptions);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const isEditing = !!itemDetails.id;
  const isArchived = itemDetails.state === "archived";

  React.useEffect(() => {
    const fetchAllData = async (): Promise<void> => {
      try {
        const [
          catalogItemDetailsRes,
          categoriesRes,
          countriesRes,
          currenciesRes,
          redemptionMethodsRes,
        ] = await Promise.all([
          id ? API.getCatalogItemDetails(id) : null,
          API.getCategories(),
          API.getCountries(),
          API.getCurrencies(),
          API.getRedemptionMethods(),
        ]);

        if (catalogItemDetailsRes) {
          const itemDetails = catalogItemDetailsRes.data.data;
          const orderedDenominations = itemDetails.denominations.sort(
            (a, b) => {
              const aPrice = parseFloat(a.priceInSubunits);
              const bPrice = parseFloat(b.priceInSubunits);
              if (aPrice < bPrice) {
                return -1;
              }
              if (aPrice > bPrice) {
                return 1;
              }
              return 0;
            }
          );

          setItemDetails({
            ...itemDetails,
            countries: itemDetails.countries.map((country) => {
              return {
                ...country,
                label: `${country.isoAlpha2Code}  ${country.name}`,
              };
            }),
            denominations: orderedDenominations.map((denomination) => {
              // NOTE: display amount needs to take into account the currency exponent
              const exponent = itemDetails.currency.exponent;
              return {
                ...denomination,
                amount: (
                  denomination.priceInSubunits /
                  10 ** exponent
                ).toString(),
              };
            }),
          });
        }

        setCategories(categoriesRes.data.data);

        setCountries(
          countriesRes.data.data.map((country) => {
            return {
              ...country,
              label: `${country.isoAlpha2Code}  ${country.name}`,
            };
          })
        );

        setCurrencies(
          currenciesRes.data.data.map((currency) => {
            return {
              id: currency.id,
              exponent: currency.exponent,
              name: `${currency.name} (${currency.iso4217Code}) ${currency.symbol}`,
              symbol: currency.symbol,
            };
          })
        );

        setRedemptionMethods(redemptionMethodsRes.data.data);
      } catch (error) {
        const message = getErrorMessage(error);
        setItemDetails(JSON.parse(JSON.stringify(defaultCatalogItemDetails)));
        setCategories([]);
        setCountries([]);
        setCurrencies([]);
        setRedemptionMethods([]);
        setErrorMessage(
          `An error occurred while loading the data needed for this page. ${message}`
        );
        datadogRum.addError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [id]);

  const handleFieldChange = (updatedFields): void => {
    setItemDetails({
      ...itemDetails,
      ...updatedFields,
    });
  };

  const hasAllRequiredFields = (): boolean => {
    const requiredFields = getRequiredFields(itemDetails);

    const everyFieldHasValue = requiredFields.every((field) => {
      return hasValue(itemDetails[field]);
    });

    // denominations amount is required
    const everyDenominationHasAmount = itemDetails.denominations.every(
      (denomination) => {
        return hasValue(denomination.amount);
      }
    );

    const hasLogo = !!(itemDetails.logo && itemDetails.logo.imageName);

    // all countries OR countries.length
    const hasCountries =
      itemDetails.forAllCountries || itemDetails.countries.length > 0;

    return (
      everyFieldHasValue &&
      everyDenominationHasAmount &&
      hasLogo &&
      hasCountries
    );
  };

  const handleSaveClick = async (event): Promise<void> => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      const defaultRedemptionMethod = redemptionMethods.find((method) => {
        return method.key === "instructionless";
      });
      const dataToSubmit = getFormattedData(
        itemDetails,
        defaultRedemptionMethod
      );
      let newItem;
      let message;

      if (isEditing) {
        newItem = await API.updateCatalogItem(itemDetails.id, dataToSubmit);
        message = `${newItem.data.data.brandName} updated in rewards catalog successfully.`;
        const exponent = newItem.data.data.currency.exponent;

        setItemDetails({
          ...newItem.data.data,
          countries: newItem.data.data.countries.map((country) => {
            return {
              ...country,
              label: `${country.isoAlpha2Code}  ${country.name}`,
            };
          }),
          denominations: newItem.data.data.denominations.map((denomination) => {
            return {
              ...denomination,
              amount: (
                denomination.priceInSubunits /
                10 ** exponent
              ).toString(),
            };
          }),
        });
      } else {
        newItem = await API.createCatalogItem(dataToSubmit);

        message = (
          <StyledParagraphNoMargin>
            <a
              href={`/super_admin/items/${newItem.data.data.id}`}
              rel="noreferrer"
              target="_blank"
            >
              {newItem.data.data.brandName}
            </a>{" "}
            added to rewards catalog successfully.
          </StyledParagraphNoMargin>
        );
        setItemDetails(JSON.parse(JSON.stringify(defaultCatalogItemDetails)));
      }

      setToastMessageOptions({
        isOpen: true,
        message,
        type: "success",
      });

      window.scrollTo(0, 0);
    } catch (error) {
      datadogRum.addError(error);
      // NOTE: creation of the item may be successful but denomination creation can fail
      // handling that here
      if (error.response && error.response.data.denominationError) {
        const denomErrors = getErrorMessage(error);

        const createMessage = (
          <>
            <p>
              {error.response.data.catalogItemName} was added to rewards catalog
              successfully but an error occurred while adding denominations.
            </p>
            <p>{denomErrors}</p>
            <StyledParagraphNoMargin>
              Go to the{" "}
              <a
                href={`/super_admin/items/${error.response.data.catalogItemId}`}
                rel="noreferrer"
                target="_blank"
              >
                item page
              </a>{" "}
              and try adding denominations again.
            </StyledParagraphNoMargin>
          </>
        );

        const editMessage = (
          <>
            <p>
              {error.response.data.catalogItemName} was updated successfully in
              the rewards catalog but an error occurred while adding
              denominations.
            </p>
            <p>{denomErrors}</p>
            <StyledParagraphNoMargin>
              Refresh this page and try again.
            </StyledParagraphNoMargin>
          </>
        );

        if (!isEditing) {
          // NOTE: if creating reset the form so they do not accidentally create a new item and show a warning message
          // with a link to the item page so they can easily edit the new item
          setItemDetails(JSON.parse(JSON.stringify(defaultCatalogItemDetails)));
        }
        window.scrollTo(0, 0);
        setToastMessageOptions({
          isOpen: true,
          message: isEditing ? editMessage : createMessage,
          type: "warning",
        });
      } else {
        const action = isEditing ? "updating the" : "creating the";
        const errorMessage = getErrorMessage(error);
        const message = `An error occurred while ${action} catalog item. ${errorMessage}`;

        setToastMessageOptions({
          isOpen: true,
          message,
          type: "error",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelClick = (): void => {
    window.location.href = "/super_admin/items";
  };

  const handleProviderChange = (event): void => {
    const newProvider = event.target.value;
    const keepExternalIdentifier = externalIdentifierProviders.includes(
      newProvider
    );
    const keepRedemptionMethod = redemptionMethodProviders.includes(
      newProvider
    );

    const updatedFields = {
      claimUrl: claimUrlIsRequired(
        itemDetails.redemptionMethod,
        itemDetails.distributionMethod,
        newProvider
      )
        ? "%{rr_redemption_url}"
        : defaultCatalogItemDetails.claimUrl,
      externalIdentifier: keepExternalIdentifier
        ? itemDetails.externalIdentifier
        : defaultCatalogItemDetails.externalIdentifier,
      legacyId: itemDetails.legacyId,
      provider: newProvider,
      redemptionMethod: keepRedemptionMethod
        ? itemDetails.redemptionMethod
        : defaultCatalogItemDetails.redemptionMethod,
      // because redemption method is being updated, the following fields that redemptionMethod controls are also updated
      redemptionLocation: keepRedemptionMethod
        ? itemDetails.redemptionLocation
        : defaultCatalogItemDetails.redemptionLocation,
      website: keepRedemptionMethod
        ? itemDetails.website
        : defaultCatalogItemDetails.website,
    };

    handleFieldChange(updatedFields);
  };

  const handleCountriesRadioChange = (event): void => {
    const radioValue = event.target.value;

    if (radioValue === "all") {
      setItemDetails({
        ...itemDetails,
        countries: [],
        forAllCountries: true,
      });
    } else if (radioValue === "select") {
      setItemDetails({
        ...itemDetails,
        countries: [...defaultCatalogItemDetails.countries],
        forAllCountries: false,
      });
    }
  };

  const handleCurrencyChange = (event): void => {
    const newCurrency = currencies.find((currency) => {
      return currency.id === event.target.value;
    });
    handleFieldChange({
      currency: newCurrency,
    });
  };

  const handleDistributionMethodChange = (event): void => {
    const newDistributionMethod = event.target.value;
    const keepTwitterHandle = twitterHandleDistributionMethods.includes(
      newDistributionMethod
    );
    const keepWebsite =
      websiteRedemptionMethods.includes(itemDetails.redemptionMethod.key) ||
      websiteDistributionMethods.includes(newDistributionMethod);

    const updatedFields = {
      claimUrl: claimUrlIsRequired(
        itemDetails.redemptionMethod,
        newDistributionMethod,
        itemDetails.provider
      )
        ? "%{rr_redemption_url}"
        : defaultCatalogItemDetails.claimUrl,
      distributionMethod: newDistributionMethod,
      twitterHandle: keepTwitterHandle
        ? itemDetails.twitterHandle
        : defaultCatalogItemDetails.twitterHandle,
      website: keepWebsite
        ? itemDetails.website
        : defaultCatalogItemDetails.website,
    };

    handleFieldChange(updatedFields);
  };

  const handleLogoChange = (event): void => {
    if (event.target.files[0]) {
      const file = event.target.files[0];

      // NOTE: encoding the file using the FileReader API
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;

        // NOTE: reader.result can be type string or ArrayBuffer and this only works if its a string
        if (typeof result !== "string") {
          throw new Error("Unexpected result from FileReader");
        }
        // NOTE: using regex to remove data url part
        const base64String = result.replace("data:", "").replace(/^.+,/, "");

        handleFieldChange({
          logo: {
            altText: `${itemDetails.brandName} logo`,
            id: itemDetails.logo && itemDetails.logo.id,
            imageBase64String: base64String,
            imageContentType: file.type,
            imageName: file.name,
            imageUrl: URL.createObjectURL(file),
          },
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRedemptionMethodChange = (event): void => {
    const newRedemptionMethod = redemptionMethods.find((method) => {
      return method.id === event.target.value;
    });

    const keepRedemptionLocation = redemptionLocationMethods.includes(
      newRedemptionMethod.key
    );
    const keepWebsite =
      websiteRedemptionMethods.includes(newRedemptionMethod.key) ||
      websiteDistributionMethods.includes(itemDetails.distributionMethod);

    const updatedFields = {
      claimUrl: claimUrlIsRequired(
        newRedemptionMethod,
        itemDetails.distributionMethod,
        itemDetails.provider
      )
        ? "%{rr_redemption_url}"
        : defaultCatalogItemDetails.claimUrl,
      redemptionLocation: keepRedemptionLocation
        ? itemDetails.redemptionLocation
        : defaultCatalogItemDetails.redemptionLocation,
      redemptionMethod: newRedemptionMethod,
      website: keepWebsite
        ? itemDetails.website
        : defaultCatalogItemDetails.website,
    };

    handleFieldChange(updatedFields);
  };

  const redemptionMethodHelperText = (
    <span>
      Click{" "}
      <a
        href="https://bonusly1612893911.atlassian.net/wiki/spaces/THOR/pages/157057083/Redemption+Types"
        rel="noreferrer"
        target="_blank"
      >
        here
      </a>{" "}
      for examples of what each redemption method looks like.
    </span>
  );

  const fileUploadHelperText = (
    <StyledParagraphNoMargin>
      Upload pngs or jpegs. Images should be 600x380 px for best results.
    </StyledParagraphNoMargin>
  );

  return (
    <StyledContainer>
      <StyledBackLink>
        <i className="fa fa-arrow-left" />
        <a data-testid="back-to-catalog-link" href="/super_admin/items">
          Back to Catalog
        </a>
      </StyledBackLink>
      <StyledPageHeader>Catalog Item Details</StyledPageHeader>
      {loading && (
        <StyledPageLoaderContainer data-testid="loader">
          <ScaleLoader
            color={theme.color.grayDark}
            loading={loading}
            height={45}
            width={6}
            margin={6}
          />
        </StyledPageLoaderContainer>
      )}

      {!loading && errorMessage && (
        <StyledErrorMessage data-testid="error-message">
          {errorMessage}
        </StyledErrorMessage>
      )}

      {!loading && !errorMessage && (
        <StyledForm data-testid="catalog-item-form">
          {isArchived && (
            <StyledAlertTextBlock data-testid="archived-alert">
              <i className="fas fa-exclamation-triangle" />
              <div className="warning-text">
                <p>
                  <b>
                    This item has been deleted from Bonusly's global catalog.
                  </b>
                </p>
                <p>
                  You can no longer make changes. Create a new reward to modify
                  this catalog item.
                </p>
              </div>
            </StyledAlertTextBlock>
          )}
          <StyledInstruction>
            All fields marked with <StyledRequired>*</StyledRequired> are
            required.
          </StyledInstruction>

          <StyledFormField>
            <TextInputField
              disabled={isArchived}
              handleChange={(event): void => {
                handleFieldChange({ brandName: event.target.value });
              }}
              helperText="Enter Brand Name from the Tango spreadsheet. Visible to end users in the
                          rewards catalog. Used in most admin and super admin experiences."
              id="brandName-input"
              isRequired
              label="Display Name"
              placeholderText="Airbnb"
              value={itemDetails.brandName}
            />
          </StyledFormField>

          <StyledFormField>
            <TextInputField
              disabled={isArchived}
              handleChange={(event): void => {
                handleFieldChange({ fullName: event.target.value });
              }}
              helperText="Enter Reward Name from the Tango spreadsheet. Limited use in the rewards
                          csv export and Reward Balance table admin experiences."
              id="fullName-input"
              isRequired
              label="Full Name"
              placeholderText="Airbnb Gift Card"
              value={itemDetails.fullName}
            />
          </StyledFormField>

          <StyledFormField>
            <RadioGroupInputField
              disabled={isArchived || isEditing}
              handleChange={handleProviderChange}
              isRequired
              id="provider"
              label="Provider"
              options={providers}
              value={itemDetails.provider}
            />
          </StyledFormField>

          {legacyIdProviders.includes(itemDetails.provider) && (
            <StyledFormField>
              <TextInputField
                disabled={isArchived}
                handleChange={(event): void => {
                  handleFieldChange({ legacyId: event.target.value });
                }}
                helperText="Enter the Legacy ID for the catalog item. This is only required for Bulk Donations"
                id="legacyId-input"
                isRequired
                label="Legacy ID"
                placeholderText="123456"
                value={itemDetails.legacyId}
              />
            </StyledFormField>
          )}

          {externalIdentifierProviders.includes(itemDetails.provider) && (
            <StyledFormField>
              <TextInputField
                disabled={isArchived || isEditing}
                handleChange={(event): void => {
                  handleFieldChange({ externalIdentifier: event.target.value });
                }}
                helperText="This ID is supplied by the reward provider, examples: Brand ID for Tango
                            or SKU for Cadooz."
                id="externalIdentifier-input"
                isRequired
                label="Brand ID"
                placeholderText="B123456"
                value={itemDetails.externalIdentifier}
              />
            </StyledFormField>
          )}

          <StyledFormField>
            <FileUploadField
              disabled={isArchived}
              fileAltText={itemDetails.logo && itemDetails.logo.altText}
              fileName={itemDetails.logo && itemDetails.logo.imageName}
              fileUrl={itemDetails.logo && itemDetails.logo.imageUrl}
              handleChange={handleLogoChange}
              handleDelete={(): void => {
                handleFieldChange({
                  logo: {
                    ...defaultCatalogItemDetails.logo,
                  },
                });
              }}
              helperText={fileUploadHelperText}
              id="logo"
              isRequired
              label="Logo"
            />
          </StyledFormField>

          <StyledFormField>
            <RadioGroupInputField
              disabled={isArchived}
              handleChange={handleDistributionMethodChange}
              id="distributionMethod"
              isRequired
              label="Type"
              options={distributionMethods}
              value={itemDetails.distributionMethod}
            />
          </StyledFormField>

          <StyledFormField>
            <Description
              disabled={isArchived}
              handleFieldChange={handleFieldChange}
              value={itemDetails.description}
            />
          </StyledFormField>

          <StyledFormField>
            <Warnings
              disabled={isArchived}
              handleFieldChange={handleFieldChange}
              value={itemDetails.warnings}
            />
          </StyledFormField>

          <StyledFormField>
            <CheckboxGroupInputField
              disabled={isArchived}
              handleChange={(checkedIds): void => {
                handleFieldChange({ categories: checkedIds });
              }}
              helperText='Generally only one category is used for a catalog item. For providers "PayPal"
                          and "Bonusly Points Boost" the category is "Cash & Points Boost".
                          For provider "Bonusly Bulk Donation" the category is "Donations".'
              id="categories-checkbox-group"
              isRequired
              label="Categories"
              options={categories}
              selectedOptions={itemDetails.categories}
            />
          </StyledFormField>

          {redemptionMethodProviders.includes(itemDetails.provider) && (
            <StyledFormField>
              <RadioGroupInputField
                disabled={isArchived}
                handleChange={handleRedemptionMethodChange}
                helperText={redemptionMethodHelperText}
                id="redemptionMethod"
                isRequired
                label="Redemption Method"
                options={redemptionMethods}
                value={itemDetails.redemptionMethod.id}
              />
            </StyledFormField>
          )}

          {claimUrlIsRequired(
            itemDetails.redemptionMethod,
            itemDetails.distributionMethod,
            itemDetails.provider
          ) && (
            <>
              <StyledFormField>
                <TextInputField
                  disabled={isArchived}
                  handleChange={(event): void => {
                    handleFieldChange({ claimUrl: event.target.value });
                  }}
                  helperText='Enter "%{rr_redemption_url}" for most catalog items. To add query params to a URL,
                              enter the full URL with the query params
                              "https://www.amazon.de/gp/css/gc/payment/view-gc-balance?claimCode=%{rr_number}" for example.
                              Note: variables wrapped in "%{}" will be replaced with their actual values on the backend.'
                  id="claimUrl-input"
                  isRequired
                  label="Claim URL"
                  placeholderText="%{rr_redemption_url}"
                  value={itemDetails.claimUrl}
                />
              </StyledFormField>
              <StyledFormField>
                <StyledLabel htmlFor="claimUrlForceTrailingSlash-checkbox">
                  Force Trailing Slash
                </StyledLabel>
                <StyledCheckboxWrapper>
                  <Checkbox
                    id="claimUrlForceTrailingSlash-checkbox"
                    checked={itemDetails.claimUrlForceTrailingSlash}
                    disabled={isArchived}
                    handleChange={(event): void => {
                      handleFieldChange({
                        claimUrlForceTrailingSlash: event.target.checked,
                      });
                    }}
                    label="Add a slash to the end of the Claim URL"
                  />
                </StyledCheckboxWrapper>
                <StyledHelperTextWithMargin>
                  Generally this is not checked. Only check this if the URL from
                  Tango is not working without a trailing slash.
                </StyledHelperTextWithMargin>
              </StyledFormField>
            </>
          )}

          {redemptionLocationMethods.includes(
            itemDetails.redemptionMethod.key
          ) && (
            <StyledFormField>
              <TextInputField
                disabled={isArchived}
                handleChange={(event): void => {
                  handleFieldChange({ redemptionLocation: event.target.value });
                }}
                helperText='Used to fill in the blank for instructions: "Redeem within ____ with the
                            code 1234". Examples include: "the App store", "any participating restaurant", "the iTunes store".'
                id="redemptionLocation-input"
                isRequired
                label="Redemption Location"
                placeholderText="the Uber app"
                value={itemDetails.redemptionLocation}
              />
            </StyledFormField>
          )}

          {(websiteRedemptionMethods.includes(
            itemDetails.redemptionMethod.key
          ) ||
            websiteDistributionMethods.includes(
              itemDetails.distributionMethod
            )) && (
            <StyledFormField>
              <TextInputField
                disabled={isArchived}
                handleChange={(event): void => {
                  handleFieldChange({ website: event.target.value });
                }}
                helperText="Enter the brand's website."
                id="website-input"
                isRequired
                label="Website"
                placeholderText="https://www.example.com"
                value={itemDetails.website}
              />
            </StyledFormField>
          )}

          {twitterHandleDistributionMethods.includes(
            itemDetails.distributionMethod
          ) && (
            <StyledFormField>
              <TextInputField
                disabled={isArchived}
                handleChange={(event): void => {
                  handleFieldChange({ twitterHandle: event.target.value });
                }}
                helperText='Do not include the "@" symbol.'
                id="twitterHandle-input"
                isRequired
                label="Twitter Handle"
                placeholderText="girlswhocode"
                value={itemDetails.twitterHandle}
              />
            </StyledFormField>
          )}

          <StyledFormField>
            <RadioGroupInputField
              disabled={isArchived}
              handleChange={handleCountriesRadioChange}
              isRequired
              id="countries"
              label="Countries"
              options={countriesRadioOptions}
              value={itemDetails.forAllCountries ? "all" : "select"}
            />
            {!itemDetails.forAllCountries && (
              <AutocompleteInputField
                disabled={isArchived}
                handleChange={(event, newValue): void => {
                  handleFieldChange({
                    countries: newValue,
                  });
                }}
                id="countries-autocomplete"
                options={countries}
                selectedOptions={itemDetails.countries}
              />
            )}
          </StyledFormField>

          <StyledFormField>
            <SelectInputField
              disabled={isArchived}
              handleChange={handleCurrencyChange}
              helperText="Select only one currency per catalog item"
              id="currency-select"
              isRequired
              label="Currency"
              options={currencies}
              value={itemDetails.currency.id}
            />
          </StyledFormField>

          <StyledFormField>
            <TextInputField
              disabled={isArchived}
              handleChange={(event): void => {
                handleFieldChange({ feePercentage: event.target.value });
              }}
              helperText="Enter a decimal representing the % of additional cost to be added to the reward cost.
                        Ex: .04 would make a 4% fee."
              id="feePercentage-input"
              label="Fee Percentage"
              placeholderText="0"
              value={itemDetails.feePercentage.toString()}
            />
          </StyledFormField>

          <StyledFormField>
            <TextInputField
              disabled={isArchived}
              handleChange={(event): void => {
                handleFieldChange({ flatFeeInUsdCents: event.target.value });
              }}
              helperText="Enter a whole number representing a flat fee in USD Cents that will be added to the reward cost.
                        Ex: 25 would make a $0.25 fee"
              id="flatFee-input"
              label="Flat Fee"
              placeholderText="0"
              value={itemDetails.flatFeeInUsdCents.toString()}
            />
          </StyledFormField>

          <StyledFormField>
            <Denominations
              currencySymbol={itemDetails.currency.symbol}
              denominations={itemDetails.denominations}
              disabled={isArchived}
              handleFieldChange={handleFieldChange}
              isEditing={isEditing}
            />
          </StyledFormField>

          <StyledFormField>
            <StyledLabel htmlFor="announceable-checkbox">Announce</StyledLabel>
            <StyledCheckboxWrapper>
              <Checkbox
                checked={itemDetails.announceable}
                disabled={isArchived}
                handleChange={(event): void => {
                  handleFieldChange({ announceable: event.target.checked });
                }}
                id="announceable-checkbox"
                label="Let company admins know when this new catalog item has been added"
              />
            </StyledCheckboxWrapper>
            <StyledHelperTextWithMargin>
              Generally this is checked.
            </StyledHelperTextWithMargin>
          </StyledFormField>

          <StyledFormField>
            <StyledLabel htmlFor="backfillable-checkbox">Backfill</StyledLabel>
            <StyledCheckboxWrapper>
              <Checkbox
                checked={itemDetails.backfillable}
                disabled={isArchived}
                handleChange={(event): void => {
                  handleFieldChange({ backfillable: event.target.checked });
                }}
                id="backfillable-checkbox"
                label="Automatically add the new catalog item to company catalogs"
              />
            </StyledCheckboxWrapper>
            <StyledHelperTextWithMargin>
              Generally this is checked.
            </StyledHelperTextWithMargin>
          </StyledFormField>

          <StyledFormField>
            <StyledLabel htmlFor="promoted-checkbox">Promoted</StyledLabel>
            <StyledCheckboxWrapper>
              <Checkbox
                checked={itemDetails.promoted}
                disabled={false}
                handleChange={(event): void => {
                  handleFieldChange({ promoted: event.target.checked });
                }}
                id="promotable-checkbox"
                label="Promote this item"
              />
            </StyledCheckboxWrapper>
          </StyledFormField>

          <StyledFormField>
            <TextInputField
              disabled={isArchived}
              handleChange={(event): void => {
                handleFieldChange({
                  effectiveAt: event.target.value.replace(/[^\d\/]+/g, ""),
                });
              }}
              helperText="Optional. Unless otherwise specified the catalog item will activate immediately. Enter as MM/DD/YYYY format."
              id="effectiveAt-input"
              isRequired={false}
              label="Release Date"
              placeholderText="MM/DD/YYYY"
              value={
                itemDetails.effectiveAt === null ? "" : itemDetails.effectiveAt
              }
            />
          </StyledFormField>

          <StyledButtonContainerHalf>
            {itemDetails.state !== "archived" && (
              <>
                <StyledButton100
                  onClick={handleCancelClick}
                  size="small"
                  testId="cancel-button"
                  variant="secondary"
                >
                  Cancel
                </StyledButton100>
                <StyledButton100
                  disabled={!hasAllRequiredFields() || isSubmitting}
                  onClick={handleSaveClick}
                  size="small"
                  testId="save-button"
                  variant="primary"
                >
                  Save
                </StyledButton100>
              </>
            )}

            {isEditing && itemDetails.state !== "archived" && (
              <StyledButtonDeleteItem
                onClick={() => setModal("delete-confirm")}
                size="small"
                testId="delete-button"
                variant=""
              >
                Delete
              </StyledButtonDeleteItem>
            )}
          </StyledButtonContainerHalf>
        </StyledForm>
      )}
      <ToastMessage
        closeToastMessage={(): void => {
          setToastMessageOptions({
            ...toastMessageOptions,
            isOpen: false,
          });
        }}
        isOpen={toastMessageOptions.isOpen}
        messageType={toastMessageOptions.type}
      >
        <span>{toastMessageOptions.message}</span>
      </ToastMessage>
      {modal === "delete-confirm" && (
        <DeleteConfirmModal
          catalogItemId={itemDetails.id}
          closeModal={() => setModal("")}
          handleFieldChange={handleFieldChange}
        />
      )}
    </StyledContainer>
  );
};

export default CatalogItemDetails;
